// Must stay the first import: it hands the config to the packages. Before it only the modules it
// imports itself run, and none of them asks for the config.
import "./services/configuration/installTerminalConfig";

// Polyfill Buffer for aws-appsync-subscription-link

import { Buffer } from "buffer";
import { Global } from "@emotion/react";
import { selectInventoryTracking, setInventoryTracking } from "@jidlomat/terminal-core/slices/fridgeInfoSlice";
import { store } from "@jidlomat/terminal-core/store";
import { readInventoryTrackingFromUrl } from "@jidlomat/terminal-core/utils/browser/inventoryTrackingSwitch";
import { installNetworkDebug } from "@jidlomat/terminal-core/utils/debug/installNetworkDebug";
import { installDuplicateInstanceDetector } from "@jidlomat/terminal-core/utils/duplicateInstanceDetector";
import { installSubscriptionLivenessTap } from "@jidlomat/terminal-core/utils/installSubscriptionLivenessTap";
import EAT_PERFECT_THEME from "@jidlomat/terminal-ui/utils/theme";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { App } from "./App";
import { ConnectionErrorModal } from "./components/global/modals/ConnectionErrorModal";
import { AnalyticsContextProvider } from "./contexts/AnalyticsContext";
import { resolveFlowKind } from "./flow/loadFlow";
import { initSentry } from "./services/external/sentry";

window.Buffer = Buffer;

// TEMP network diagnostic: install the fetch wrapper + connectivity listeners before any request runs.
// Self-gated by the ?networkDebug flag, so it's a no-op on normal customer tablets.
installNetworkDebug();
// Observe inbound AppSync `ka`/frames for the liveness watchdog. Must run before the Apollo client
// opens its WebSocket, so install it here at bootstrap.
installSubscriptionLivenessTap();
// What the fridge tracks, as far as its URL says. The flow loader, the stock selectors and the
// analytics read it from the first render on; the backend does not report it yet.
store.dispatch(setInventoryTracking(readInventoryTrackingFromUrl()));

const enableMocking = async () => {
	// Read straight from the build environment and compared with a literal: a build where the variable
	// is not "true" drops this branch, and with it the mock server never becomes part of the bundle.
	if (import.meta.env.VITE_MSW_ENABLED !== "true") {
		return;
	}

	const { worker } = await import("@jidlomat/terminal-test-utils/mocks/browser");
	await worker.start();
	window.MSW = {};
};

initSentry();
// Count sibling app instances of this origin (ghost webviews leaked by the kiosk shell) and tag
// every Sentry event with instanceId + duplicateInstances, so the ghost population is queryable
// fleet-wide. Passive (BroadcastChannel announce/farewell) — no polling. After initSentry so the
// tags land on the initialized scope.
installDuplicateInstanceDetector();

enableMocking().then(() => {
	createRoot(document.getElementById("root") as HTMLElement).render(
		<StrictMode>
			<ThemeProvider theme={EAT_PERFECT_THEME}>
				<CssBaseline />
				<Global
					styles={{
						".MuiDrawer-root > .MuiPaper-root": {
							height: `calc(50% - ${320}px)`,
							overflow: "visible",
						},
						body: {
							overflow: "unset",
						},
					}}
				/>
				<Provider store={store}>
					<AnalyticsContextProvider flow={resolveFlowKind(selectInventoryTracking(store.getState()))}>
						<SentryErrorBoundary fallback={<ConnectionErrorModal appConnected={false} />}>
							<App />
						</SentryErrorBoundary>
					</AnalyticsContextProvider>
				</Provider>
			</ThemeProvider>
		</StrictMode>,
	);
});
